import React, { Fragment, useEffect, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Grid, MenuItem, Paper, TextField } from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Remove } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import CustomButton from '../../../components/CustomButton';
import BuilderSearch from '../../../components/BulderSearch/BuilderSearch';
import PlusIcon from '../../../components/Icons/PlusIcon';
import HubTypeSelect from '../../../components/HubTypeSelect';
import {
  beautifyErrors,
  transformGraphQlErrorMessage,
  ValidationErrors,
} from '../../../utils/helpers';
import Builder from '../../../types/Builder';
import { AvailableHubOption } from '../../../types/Devices';
import InputDevice from '../../../types/inputTypes/Device';
import {
  GET_HUBACCOUNT_IDS,
  UPDATE_DEVICES,
} from '../../../graphql/queries/Devices';
import AvailableHubSearch from '../DeviceForm/AvailableHubSearch';
import { assignmentSchema } from './BatchAssignValidation';
import useStyles from './styles';

type Props = RouteComponentProps;

type BatchAssignResult = {
  updateMultipleHubs: InputDevice[];
};

const BatchAssign: React.FC<Props> = ({ history }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [assignment, setAssignment] = useState<InputDevice>(new InputDevice());
  const [selectedDevices, setSelectedDevices] = useState<InputDevice[]>([new InputDevice()]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors<InputDevice>>({});
  const [validationSelectedDeviceErrors, setValidationSelectedDeviceErrors] = useState<ValidationErrors<InputDevice>[]>([]);
  const [hubAccounts, setHubAccounts] = useState<Array<{ id: string; username: string }>>([]);

  const { data: hubAccountData } = useQuery<{ hubAccountIds: Array<{ id: string; username: string }> }>(
    GET_HUBACCOUNT_IDS,
  );

  useEffect(() => {
    if (hubAccountData) {
      setHubAccounts(hubAccountData.hubAccountIds);
    }
  }, [hubAccountData]);

  const [updateMultipleHubs, { loading }] = useMutation<BatchAssignResult>(
    UPDATE_DEVICES,
    { errorPolicy: 'all' },
  );

  const handleAssignmentChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    setAssignment(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleBuilderChange = (builder: Builder) => {
    setAssignment(prevState => ({
      ...prevState,
      builder_id: builder.builder_id,
    }));
  };

  const handleAddDevice = () => {
    setSelectedDevices(prevDevices => [...prevDevices, new InputDevice()]);
  };

  const handleRemoveDevice = (index: number) => {
    setSelectedDevices(prevDevices => prevDevices.filter((_, currentIndex) => currentIndex !== index));
  };

  const formatMacAddress = (input: string): string => {
    const hexOnly = input.replace(/[^a-fA-F0-9]/g, '').toUpperCase();
    const trimmed = hexOnly.substring(0, 12);
    const parts = trimmed.match(/.{1,2}/g);
    return parts ? parts.join(':') : '';
  };

  const handleAvailableHubChange = (index: number, hub: AvailableHubOption | null) => {
    setSelectedDevices(prevDevices => {
      const updatedDevices = [...prevDevices];
      updatedDevices[index] = {
        ...updatedDevices[index],
        id: hub?.id || '',
        serial_number: hub?.serial_number || '',
        mac: formatMacAddress(hub?.description || ''),
      };
      return updatedDevices;
    });
  };

  const handleAssign = async () => {
    const errorsPerDevice: ValidationErrors<InputDevice>[] = selectedDevices.map(() => ({}));
    let generalErrors: ValidationErrors<InputDevice> = {};
    let hasErrors = false;

    try {
      assignmentSchema.validateSync(assignment, { abortEarly: false });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        hasErrors = true;
        generalErrors = beautifyErrors<InputDevice>(error);
      }
    }

    selectedDevices.forEach((device, index) => {
      if (!device.serial_number) {
        hasErrors = true;
        errorsPerDevice[index].serial_number = 'Serial number field is require';
      }
    });

    const selectedIds = selectedDevices.map(device => device.id).filter(Boolean);
    const uniqueIds = new Set(selectedIds);

    if (selectedIds.length !== selectedDevices.length || uniqueIds.size !== selectedIds.length) {
      hasErrors = true;

      selectedDevices.forEach((device, index) => {
        if (!device.id) {
          errorsPerDevice[index].serial_number = 'Please select a serial number from the available list';
        } else if (selectedIds.filter(id => id === device.id).length > 1) {
          errorsPerDevice[index].serial_number = 'Serial number is duplicated in the batch';
        }
      });
    }

    if (hasErrors) {
      enqueueSnackbar('Validation error', { variant: 'error' });
      setValidationErrors(generalErrors);
      setValidationSelectedDeviceErrors(errorsPerDevice);
      return;
    }

    setValidationErrors({});
    setValidationSelectedDeviceErrors([]);

    try {
      const devices = selectedDevices.map(device => ({
        serial_number: device.serial_number,
        builder_id: assignment.builder_id,
        hub_account_id: assignment.hub_account_id,
        hub_type: assignment.hub_type,
      }));

      const result = await updateMultipleHubs({
        variables: {
          devices,
        },
      });

      const payload = result.data?.updateMultipleHubs;

      if (!payload) {
        enqueueSnackbar('Batch assign failed', { variant: 'error' });
        return;
      }

      enqueueSnackbar(
        `${payload.length} hub(s) assigned.`,
        { variant: 'success' },
      );

      setSelectedDevices([new InputDevice()]);
      history.push('/devices');
    } catch (error) {
      enqueueSnackbar(transformGraphQlErrorMessage((error as Error).message), {
        variant: 'error',
      });
    }
  };

  const renderDeviceFields = (item: InputDevice, index: number) => {
    const errors = validationSelectedDeviceErrors[index] || {};

    return (
      <Fragment key={`${item.id || item.serial_number || 'device'}-${index}`}>
        <Grid item xs={12} sm={6}>
          <AvailableHubSearch
            margin="normal"
            id={`serial_number_${index}`}
            name="serial_number"
            value={{
              id: item.id || '',
              serial_number: item.serial_number,
              description: item.mac || '',
              imei: item.imei || '',
            }}
            onHubChange={hub => handleAvailableHubChange(index, hub)}
            helperText={errors.serial_number || ''}
            error={!!errors.serial_number}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            margin="normal"
            id={`mac_${index}`}
            name="mac"
            label="MAC"
            value={item.mac}
            helperText={errors.mac || ''}
            error={!!errors.mac}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid className={classes.multiplyDevicesIconsContainer}>
          {index + 1 === selectedDevices.length && (
            <PlusIcon
              className={classes.icon}
              onClick={handleAddDevice}
            />
          )}
          {index + 1 === selectedDevices.length && index !== 0 && (
            <Remove
              className={classes.icon}
              onClick={() => handleRemoveDevice(index)}
            />
          )}
        </Grid>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <div className={classes.actionsBar}>
        <div className={classes.actionButtonsWrapper}>
          <Button
            variant="contained"
            className={classes.backToButton}
            component={Link}
            to="/devices"
          >
            <ArrowLeftIcon />
            Devices
          </Button>
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <Grid item xs={12} sm={12}>
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <HubTypeSelect
                  margin="normal"
                  fullWidth
                  id="hub_type"
                  label="Hub Type"
                  name="hub_type"
                  select
                  hubTypes={['Ezlo Secure', 'Ezlo Wifi']}
                  value={assignment.hub_type}
                  error={!!validationErrors.hub_type}
                  helperText={validationErrors.hub_type || ''}
                  onChange={handleAssignmentChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  select
                  label="Hub Account"
                  name="hub_account_id"
                  onChange={handleAssignmentChange}
                  value={assignment.hub_account_id}
                  helperText={validationErrors.hub_account_id}
                  error={!!validationErrors.hub_account_id}
                >
                  {hubAccounts
                    .slice()
                    .sort((a, b) => a.username.localeCompare(b.username))
                    .map(hubAccount => (
                      <MenuItem
                        key={hubAccount.id}
                        value={hubAccount.id}
                      >
                        {hubAccount.username}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              {selectedDevices.map((device, index) => renderDeviceFields(device, index))}
              <Grid item xs={12} sm={12}>
                <BuilderSearch
                  margin="normal"
                  onBuilderChange={handleBuilderChange}
                  label="Builder"
                  helperText={validationErrors.builder_id}
                />
              </Grid>
              <Grid container spacing={4} justify="center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <Grid>
                  <CustomButton
                    variant="orange"
                    onClick={handleAssign}
                    loading={loading}
                  >
                    Assign
                  </CustomButton>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </div>
    </Fragment>
  );
};

export default React.memo(BatchAssign);
